import {
    type ComponentPropsWithRef,
    type MouseEvent,
    type ReactElement,
    type ReactNode,
    useEffect,
    useRef,
} from 'react';
import { NavLink } from 'react-router-dom';
import empireLogo from '../features/auth/empire-logo.webp';
import { Button, Icon, Tooltip } from '../ui';
import { type NavBadge as NavBadgeModel, type NavItem, navConfig } from './navConfig';
import styles from './Sidebar.module.css';

/**
 * The §6 left rail — the gradient sidebar: logo header, the primary `<nav>` landmark rendering
 * `navConfig`, the footer slot, and the three responsive modes (§9).
 *
 * This component and its props are the LOCKED F5-b WI-1.1 → WI-1.2 contract (F5-b WI-1.1 plan Decision 4): the
 * `footer` slot (F5-b WI-1.2 mounts the current-user menu) and the `renderNavItem` seam (F5-b WI-1.2 wraps
 * each entry in the shipped `PermissionGuard`). F5-b WI-1.1 supplies identity defaults for both and
 * renders every entry unconditionally (F5-b spec FR-5). Rendered widths, the 180ms transition, contrast
 * and the axe pass are F5-b WI-2.1's; F5-b WI-1.1 builds the mechanism (mode → class, scrim, `Esc`).
 */
export type SidebarMode = 'expanded' | 'icon' | 'overlay';

export interface SidebarProps {
    /**
     * The `<aside>`'s id, so the sidebar toggle can name the region it controls (`aria-controls`).
     * Additive to the F5-b WI-1.1 contract; omitted, the aside carries no id.
     */
    readonly id?: string;
    /** The rendered form of the rail; `AppLayout` derives it from the tier + collapse pref (F5-b WI-1.1 plan Decision 6). */
    readonly mode: SidebarMode;
    /** Whether the overlay drawer is open — only meaningful when `mode === 'overlay'`. */
    readonly overlayOpen?: boolean;
    /**
     * Called on a scrim click, the drawer's Close button or `Esc` while the overlay is open — and on a
     * nav choice too, unless
     * `onOverlayNavigate` is given.
     */
    readonly onOverlayClose?: () => void;
    /**
     * Called instead of `onOverlayClose` when a nav choice closes the open overlay, so the layout can
     * send focus to the new page rather than back to the toggle. Additive to the F5-b WI-1.1 contract;
     * omitted, a nav choice calls `onOverlayClose`.
     */
    readonly onOverlayNavigate?: () => void;
    /** The sidebar-footer slot — F5-b WI-1.1 fills it with the interim sign-out; F5-b WI-1.2 mounts the user menu. */
    readonly footer?: ReactNode;
    /**
     * Revamp (AD #39): the sidebar toggle, which lives here on every route (`AppLayout`). Drawn
     * in the brand header — at the end of the logo row when expanded, under the logo in the icon rail —
     * and not while the drawer is open, where the drawer's Close button is the way out. Omitted, the
     * header is unchanged.
     */
    readonly toggle?: ReactNode;
    /**
     * The gating seam F5-b WI-1.2 consumes: it receives each nav entry and its rendered element and
     * returns the element wrapped in the shipped `PermissionGuard`. Default is identity.
     */
    readonly renderNavItem?: (item: NavItem, element: ReactElement) => ReactNode;
    /**
     * Live badges by nav entry id, which win over an entry's static `badge` — the Activities overdue
     * count `AppLayout` feeds (F3-a WI-2.2 plan Decision 21). Additive to the F5-b WI-1.1 contract; omitted,
     * each entry shows its own badge, if any.
     */
    readonly badges?: Readonly<Record<string, NavBadgeModel>> | undefined;
}

/** The default seam: render each entry unchanged (F5-b WI-1.1 gates nothing — F5-b spec FR-5). */
function renderIdentity(_item: NavItem, element: ReactElement): ReactNode {
    return element;
}

/**
 * The §6 nav mark. A shell-local element rather than the §5 `Badge` because §2 defines no
 * `accent/gold` `Badge` tone (plan deviation 1). `data-kind` carries the tone for the mechanism
 * tests; the tone → token mapping is in the stylesheet and guarded by the F5-b AC-14 source scan.
 */
function NavBadge({ badge }: { readonly badge: NavBadgeModel }) {
    if (badge.label === undefined) {
        return (
            <span className={styles.badge} data-testid="app-nav-badge" data-kind={badge.kind}>
                {badge.value}
            </span>
        );
    }
    // The number is drawn; the words are read. In the icon rail the badge is collapsed out of view, as
    // the label is, and the hidden words keep the count in the link's name.
    return (
        <>
            <span
                aria-hidden="true"
                className={styles.badge}
                data-testid="app-nav-badge"
                data-kind={badge.kind}
            >
                {badge.value}
            </span>
            <span className="sr-only">, {badge.label}</span>
        </>
    );
}

/**
 * Whether a click navigates this tab: the primary button, no modifier, not already handled. A
 * Ctrl/Cmd/Shift/Alt click opens a new tab or window (or downloads), so the page stays where it
 * is and the drawer must stay open.
 */
function isPlainPrimaryClick(event: MouseEvent<HTMLAnchorElement>): boolean {
    return (
        !event.defaultPrevented &&
        event.button === 0 &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey &&
        !event.altKey
    );
}

const TAB_STOP_CANDIDATES = [
    'a[href]',
    'button',
    'input',
    'select',
    'textarea',
    'summary',
    '[contenteditable]:not([contenteditable="false"])',
    'audio[controls]',
    'video[controls]',
    '[tabindex]',
].join(', ');

/**
 * Whether the element is rendered. `checkVisibility()` is false for `display: none` and
 * `content-visibility` hidden subtrees; jsdom lacks it (it does no layout), and there every element
 * counts as rendered.
 */
function isRendered(element: HTMLElement): boolean {
    return typeof element.checkVisibility !== 'function' || element.checkVisibility();
}

/**
 * The drawer's Tab stops in document order: what Tab can reach inside `root`. Menu items carry
 * `tabIndex={-1}` (roving focus) and so are not stops; nothing under `inert` or `hidden`, and nothing
 * not rendered, is either.
 */
function tabStops(root: HTMLElement): HTMLElement[] {
    return [...root.querySelectorAll<HTMLElement>(TAB_STOP_CANDIDATES)].filter(
        (element) =>
            element.tabIndex >= 0 &&
            !element.matches(':disabled') &&
            element.closest('[inert], [hidden]') === null &&
            isRendered(element),
    );
}

/** Whether `later` comes after `earlier` in document order. */
function follows(earlier: Node, later: Node): boolean {
    return (earlier.compareDocumentPosition(later) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
}

/**
 * One nav row: concept icon + Title-Case label + optional badge, active-marked by `NavLink`.
 * `onChoose` is set only while the tablet drawer is open: choosing a destination in this tab
 * closes it.
 *
 * `labelTooltip` is set in the icon-only rail, where the label is collapsed out of view: the §5
 * Tooltip then shows it on hover and on keyboard focus. The label is already the link's accessible
 * name, so the tooltip does not describe the link as well (`describesTrigger={false}`) — a reader
 * would otherwise hear the label twice. The Tooltip wraps the link in every mode and is disabled
 * outside the rail, so a mode change does not remount the link.
 *
 * Any other props, the ref included, go to the link: `renderGatedNavItem` wraps the row in
 * `PermissionGuard`, whose tooltip needs the link's element and whose blocked state writes its
 * attributes onto the link (BUG-195).
 */
function NavRow({
    item,
    badge,
    onChoose,
    labelTooltip,
    ...forwarded
}: {
    readonly item: NavItem;
    /** The entry's badge — a live one from `SidebarProps.badges`, else its own. */
    readonly badge?: NavBadgeModel | undefined;
    readonly onChoose?: (() => void) | undefined;
    readonly labelTooltip: boolean;
} & Omit<ComponentPropsWithRef<'a'>, 'children' | 'className' | 'href' | 'onClick' | 'style'>) {
    const shownBadge = badge ?? item.badge;
    return (
        <Tooltip
            content={shownBadge?.label === undefined ? item.label : `${item.label}, ${shownBadge.label}`}
            placement="right"
            describesTrigger={false}
            disabled={!labelTooltip}
            data-testid="app-nav-tooltip"
        >
            <NavLink
                {...forwarded}
                to={item.path}
                className={({ isActive }) =>
                    isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
                }
                /* testid-exempt: named by its navConfig label (a span child the scan cannot read), so tests address it by role and name */
                onClick={(event) => {
                    if (onChoose !== undefined && isPlainPrimaryClick(event)) {
                        onChoose();
                    }
                }}
            >
                <span className={styles.navIcon}>
                    <Icon name={item.icon} />
                </span>
                <span className={styles.navLabel}>{item.label}</span>
                {shownBadge === undefined ? null : <NavBadge badge={shownBadge} />}
            </NavLink>
        </Tooltip>
    );
}

export function Sidebar({
    id,
    mode,
    overlayOpen = false,
    onOverlayClose,
    onOverlayNavigate,
    footer,
    toggle,
    renderNavItem = renderIdentity,
    badges,
}: SidebarProps) {
    const isOverlay = mode === 'overlay';
    const drawerOpen = isOverlay && overlayOpen;
    const asideRef = useRef<HTMLElement>(null);
    const scrimRef = useRef<HTMLButtonElement>(null);

    // Opening the drawer moves focus into it (WCAG 2.4.3): the first nav link, or — when the session
    // holds no navigation entry — the first control in the rail (the current-user menu). Returning
    // focus to the toggle on close is `AppLayout`'s, because the toggle lives in this header and is
    // rendered again only once the drawer has closed.
    useEffect(() => {
        if (drawerOpen) {
            const aside = asideRef.current;
            (
                aside?.querySelector<HTMLElement>('nav a[href]') ??
                aside?.querySelector<HTMLElement>('button')
            )?.focus();
        }
    }, [drawerOpen]);

    // Close the overlay on `Esc` (F5-b AC-8 / AC-15 mechanism). A document-level listener, so the key is
    // caught wherever focus is. Esc closes only the innermost layer: a layer inside the drawer that
    // handles Esc itself — the current-user `DropdownMenu` — calls `preventDefault`, and an event it
    // has claimed is left alone here, so Esc in the open menu closes the menu and not the drawer.
    useEffect(() => {
        if (!drawerOpen) {
            return;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.defaultPrevented) {
                return;
            }
            if (event.key === 'Escape') {
                onOverlayClose?.();
                return;
            }
            // Tab wraps inside the drawer (WCAG 2.4.3). The column behind it is `inert` and the scrim
            // is not a Tab stop, so past the drawer's last stop the browser would move focus out of
            // the page altogether; Tab from the last stop goes to the first, Shift+Tab from the first
            // to the last. Focus that has left the page (the body, or nothing) or sits on the scrim
            // after a pointer press re-enters the drawer; focus in anything else — another open
            // dialog — is left alone.
            // Deliberately unlike Modal, whose native showModal() lets Tab pass through the browser
            // chrome: the drawer is not a native modal, so it wraps itself.
            const aside = asideRef.current;
            if (event.key !== 'Tab' || aside === null) {
                return;
            }
            const stops = tabStops(aside);
            const first = stops[0];
            const last = stops.at(-1);
            const active = document.activeElement;
            if (first === undefined || last === undefined) {
                return;
            }
            const inside = active !== null && aside.contains(active);
            if (!inside && active !== null && active !== document.body && active !== scrimRef.current) {
                return;
            }
            if (event.shiftKey) {
                if (!inside || !stops.some((stop) => follows(stop, active))) {
                    event.preventDefault();
                    last.focus();
                }
            } else if (!inside || !stops.some((stop) => follows(active, stop))) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [drawerOpen, onOverlayClose]);

    return (
        <>
            {drawerOpen ? (
                // A pointer target only. Out of the Tab order and hidden from assistive tech: it covers
                // the viewport, so its focus ring would be drawn outside it (SC 2.4.7), and Esc is the
                // keyboard way to close the drawer. `tabIndex={-1}` is what makes the `aria-hidden`
                // legal on a button (axe aria-hidden-focus).
                <button
                    ref={scrimRef}
                    type="button"
                    tabIndex={-1}
                    aria-hidden="true"
                    className={styles.scrim}
                    data-testid="app-sidebar-scrim"
                    onClick={onOverlayClose}
                />
            ) : null}
            <aside
                ref={asideRef}
                id={id}
                className={styles.sidebar}
                // Revamp (AD #39): the sidebar takes the revamped colour layer on every route, so the
                // frame looks the same whichever theme the page in it uses (theme-revamp.css part 2).
                data-et-theme="revamp"
                data-mode={mode}
                data-open={isOverlay ? drawerOpen : undefined}
                aria-hidden={isOverlay && !drawerOpen ? true : undefined}
                // `inert` as well as `aria-hidden`: aria-hidden alone leaves the rail's links in the
                // tab order, so a keyboard user would tab into invisible, unannounced links (BUG-028).
                inert={isOverlay && !drawerOpen ? true : undefined}
            >
                <div
                    className={styles.brand}
                    data-has-toggle={toggle !== undefined && !drawerOpen ? true : undefined}
                >
                    {/* Decorative: the wordmark beside it carries the brand name (empty alt). */}
                    <img src={empireLogo} alt="" className={styles.logo} />
                    <span className={styles.brandText}>
                        <span className={styles.wordmark}>Empire Teas</span>
                        <span className={styles.eyebrow}>Sales CRM</span>
                    </span>
                    {/* The drawer's visible way out, a Tab stop inside the viewport (the scrim is a
                        pointer target only). Rendered only while the drawer is open; it closes the
                        drawer and focus returns to the toggle, as Esc does. */}
                    {drawerOpen ? (
                        <span className={styles.brandClose}>
                            <Button
                                variant="ghost-icon"
                                icon="close"
                                size="sm"
                                aria-label="Close navigation"
                                data-testid="app-sidebar-close"
                                onClick={onOverlayClose}
                            />
                        </span>
                    ) : null}
                    {toggle !== undefined && !drawerOpen ? (
                        <span className={styles.brandToggle}>{toggle}</span>
                    ) : null}
                </div>
                <nav aria-label="Primary" className={styles.nav}>
                    <ul className={styles.navList}>
                        {navConfig.map((item) => (
                            <li key={item.id} className={styles.navItem}>
                                {renderNavItem(
                                    item,
                                    <NavRow
                                        badge={badges?.[item.id]}
                                        item={item}
                                        onChoose={
                                            drawerOpen ? (onOverlayNavigate ?? onOverlayClose) : undefined
                                        }
                                        labelTooltip={mode === 'icon'}
                                    />,
                                )}
                            </li>
                        ))}
                    </ul>
                </nav>
                <div className={styles.footer}>{footer}</div>
            </aside>
        </>
    );
}
